import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Factory, RefreshCw, Filter, TrendingUp, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const ProductionReport = () => {
  const { api } = useAuth();
  const [productions, setProductions] = useState([]);
  const [machines, setMachines] = useState([]);
  const [moulds, setMoulds] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [parts, setParts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    machineId: '',
    mouldId: '',
    shiftId: '',
  });

  const fetchMasterData = useCallback(async () => {
    try {
      const [machinesRes, mouldsRes, shiftsRes, partsRes, employeesRes] = await Promise.all([
        api.get('/machines'),
        api.get('/moulds'),
        api.get('/shifts'),
        api.get('/parts'),
        api.get('/employees')
      ]);
      setMachines(machinesRes.data);
      setMoulds(mouldsRes.data);
      setShifts(shiftsRes.data);
      setParts(partsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Failed to fetch master data');
    }
  }, [api]);

  const fetchProductions = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/productions?';
      if (filters.fromDate) url += `from_date=${filters.fromDate}&`;
      if (filters.toDate) url += `to_date=${filters.toDate}&`;
      if (filters.machineId) url += `machine_id=${filters.machineId}&`;
      if (filters.mouldId) url += `mould_id=${filters.mouldId}&`;
      
      const response = await api.get(url);
      let data = response.data;
      
      // Client-side filter for shift if needed
      if (filters.shiftId) {
        data = data.filter(p => p.shift_id === parseInt(filters.shiftId));
      }
      
      setProductions(data);
    } catch (error) {
      toast.error('Failed to fetch production data');
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  useEffect(() => {
    fetchProductions();
  }, [fetchProductions]);

  const getMachineName = (id) => {
    const machine = machines.find(m => m.id === id);
    return machine ? `${machine.code} - ${machine.name}` : '-';
  };

  const getMouldName = (id) => {
    const mould = moulds.find(m => m.id === id);
    return mould ? mould.code : '-';
  };

  const getShiftName = (id) => {
    const shift = shifts.find(s => s.id === id);
    return shift ? shift.name : '-';
  };

  const getPartName = (id) => {
    const part = parts.find(p => p.id === id);
    return part ? part.name : '-';
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? `${emp.first_name}` : '-';
  };

  // Calculate summary statistics
  const summary = productions.reduce((acc, prod) => {
    acc.totalShots += prod.no_of_shots || 0;
    acc.totalLumps += parseFloat(prod.lumps) || 0;
    acc.totalRunner += parseFloat(prod.runner_rejections) || 0;
    
    (prod.details || []).forEach(d => {
      acc.totalProduced += parseFloat(d.produced_quantity) || 0;
      acc.totalAccepted += parseFloat(d.accepted_quantity) || 0;
      acc.totalRejected += parseFloat(d.rejected_quantity) || 0;
    });
    
    return acc;
  }, { totalShots: 0, totalLumps: 0, totalRunner: 0, totalProduced: 0, totalAccepted: 0, totalRejected: 0 });

  const acceptanceRate = summary.totalProduced > 0 
    ? ((summary.totalAccepted / summary.totalProduced) * 100).toFixed(1) 
    : 0;

  const rejectionRate = summary.totalProduced > 0 
    ? ((summary.totalRejected / summary.totalProduced) * 100).toFixed(1) 
    : 0;

  // Aggregate rejection reasons across all productions
  const rejectionBreakdown = productions.reduce((acc, prod) => {
    (prod.details || []).forEach(d => {
      (d.rejection_details || []).forEach(r => {
        acc[r.rejection_reason] = (acc[r.rejection_reason] || 0) + (parseFloat(r.quantity) || 0);
      });
    });
    return acc;
  }, {});

  const sortedRejections = Object.entries(rejectionBreakdown)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="production-report">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Factory className="w-6 h-6 text-sky-500" />
            Production Report
          </h1>
          <p className="text-slate-500 text-sm">{productions.length} production entries</p>
        </div>
        <Button
          onClick={fetchProductions}
          variant="outline"
          className="rounded-sm"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-slate-200">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs">From Date</Label>
              <Input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs">To Date</Label>
              <Input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Machine</Label>
              <Select
                value={filters.machineId || "all"}
                onValueChange={(val) => setFilters({ ...filters, machineId: val === "all" ? "" : val })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="All Machines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Machines</SelectItem>
                  {machines.map(m => (
                    <SelectItem key={m.id} value={m.id.toString()}>{m.code} - {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mould</Label>
              <Select
                value={filters.mouldId || "all"}
                onValueChange={(val) => setFilters({ ...filters, mouldId: val === "all" ? "" : val })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="All Moulds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Moulds</SelectItem>
                  {moulds.map(m => (
                    <SelectItem key={m.id} value={m.id.toString()}>{m.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Shift</Label>
              <Select
                value={filters.shiftId || "all"}
                onValueChange={(val) => setFilters({ ...filters, shiftId: val === "all" ? "" : val })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="All Shifts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Shifts</SelectItem>
                  {shifts.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Total Shots</p>
                <p className="text-xl font-bold text-slate-900">{summary.totalShots.toLocaleString()}</p>
              </div>
              <TrendingUp className="w-5 h-5 text-sky-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Produced</p>
                <p className="text-xl font-bold text-slate-900">{summary.totalProduced.toLocaleString()}</p>
              </div>
              <Factory className="w-5 h-5 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Accepted</p>
                <p className="text-xl font-bold text-green-600">{summary.totalAccepted.toLocaleString()}</p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Rejected</p>
                <p className="text-xl font-bold text-red-600">{summary.totalRejected.toLocaleString()}</p>
              </div>
              <XCircle className="w-5 h-5 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Acceptance %</p>
                <p className="text-xl font-bold text-green-600">{acceptanceRate}%</p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Rejection %</p>
                <p className="text-xl font-bold text-red-600">{rejectionRate}%</p>
              </div>
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Rejection Reasons */}
      {sortedRejections.length > 0 && (
        <Card className="mb-4 border-slate-200">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Top Rejection Reasons</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex flex-wrap gap-2">
              {sortedRejections.map(([reason, qty]) => (
                <div key={reason} className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-sm">
                  <span className="text-sm font-medium text-red-800">{reason}</span>
                  <span className="text-sm font-bold text-red-600">{qty.toFixed(0)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Production Table */}
      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm">Production Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : productions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No production records found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Prod No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Machine</TableHead>
                    <TableHead>Mould</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Operator</TableHead>
                    <TableHead className="text-right">Shots</TableHead>
                    <TableHead className="text-right">Produced</TableHead>
                    <TableHead className="text-right">Accepted</TableHead>
                    <TableHead className="text-right">Rejected</TableHead>
                    <TableHead className="text-right">Lumps</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productions.map((prod) => {
                    const totals = (prod.details || []).reduce((acc, d) => ({
                      produced: acc.produced + (parseFloat(d.produced_quantity) || 0),
                      accepted: acc.accepted + (parseFloat(d.accepted_quantity) || 0),
                      rejected: acc.rejected + (parseFloat(d.rejected_quantity) || 0),
                    }), { produced: 0, accepted: 0, rejected: 0 });
                    
                    return (
                      <TableRow key={prod.id}>
                        <TableCell className="font-mono font-bold">PROD-{prod.production_no}</TableCell>
                        <TableCell>{new Date(prod.production_date).toLocaleDateString()}</TableCell>
                        <TableCell className="text-xs">{getMachineName(prod.machine_id)}</TableCell>
                        <TableCell>{getMouldName(prod.mould_id)}</TableCell>
                        <TableCell>{getShiftName(prod.shift_id)}</TableCell>
                        <TableCell className="text-xs">{getEmployeeName(prod.operator_id)}</TableCell>
                        <TableCell className="text-right font-mono">{prod.no_of_shots}</TableCell>
                        <TableCell className="text-right font-mono">{totals.produced.toFixed(0)}</TableCell>
                        <TableCell className="text-right font-mono text-green-600">{totals.accepted.toFixed(0)}</TableCell>
                        <TableCell className="text-right font-mono text-red-600">{totals.rejected.toFixed(0)}</TableCell>
                        <TableCell className="text-right font-mono">{parseFloat(prod.lumps).toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Totals Row */}
                  <TableRow className="bg-slate-100 font-bold">
                    <TableCell colSpan={6}>TOTAL</TableCell>
                    <TableCell className="text-right font-mono">{summary.totalShots.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">{summary.totalProduced.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono text-green-600">{summary.totalAccepted.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{summary.totalRejected.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">{summary.totalLumps.toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductionReport;
